import { createContext, useContext, useState } from 'react'
import TrackModal from '../track/TrackModal'
import AlbumModal from '../track/AlbumModal'
import RoomDetailsModal from '../rooms/RoomDetailsModal'
import CreateRoomModal from '../rooms/CreateRoom'
import RoomsModal from '../rooms/RoomsModal'
import LibraryModal from '../library/LibraryModal'
import UploadModal from '../upload/UploadModal'
import SearchModal from '../search/SearchModal'
import DigModal from '../search/DigModal'
import ProfileModal from '../profile/ProfileModal'
import ConfirmEmailModal from '../profile/ConfirmEmailModal'
import AlertModal from './AlertModal'
import ConfirmModal from './ConfirmModal'
import ProgressModal from './ProgressModal'
import VerificationRequiredModal from '../login/VerificationRequiredModal'
import UnverifiedAccountModal from '../login/UnverifiedAccountModal'
import EmailLoginModal from '../login/EmailLoginModal'
import EmailCreateAccountModal from '../login/EmailCreateAccountModal'
import VerifyingModal from '../login/VerifyingModal'
import AcceptingInviteModal from '../login/AcceptingInviteModal'
import ForgotPasswordModal from '../login/ForgotPasswordModal'
import ResetPasswordModal from '../login/ResetPasswordModal'
import DJChooserModal from '../rooms/DJChooser'
import ReportTrackProblemModal from '../player/ReportTrackProblemModal'
import YouTubeSearchModal from '../search/YouTubeSearchModal'
import ShareModal from '../rooms/ShareModal'

const ModalStackContext = createContext()

// Map modal types to components
const MODAL_COMPONENTS = {
    track: TrackModal,
    album: AlbumModal,
    roomDetails: RoomDetailsModal,
    createRoom: CreateRoomModal,
    rooms: RoomsModal,
    library: LibraryModal,
    upload: UploadModal,
    search: SearchModal,
    dig: DigModal,
    profile: ProfileModal,
    confirmEmail: ConfirmEmailModal,
    alert: AlertModal,
    confirm: ConfirmModal,
    progress: ProgressModal,
    verificationRequired: VerificationRequiredModal,
    unverifiedAccount: UnverifiedAccountModal,
    emailLogin: EmailLoginModal,
    emailCreateAccount: EmailCreateAccountModal,
    verifying: VerifyingModal,
    acceptingInvite: AcceptingInviteModal,
    forgotPassword: ForgotPasswordModal,
    resetPassword: ResetPasswordModal,
    djChooser: DJChooserModal,
    reportTrackProblem: ReportTrackProblemModal,
    youtubeSearch: YouTubeSearchModal,
    share: ShareModal,
}

export function ModalStackProvider({ children }) {
    const [modalStack, setModalStack] = useState([])
    const [openingModals, setOpeningModals] = useState(new Set())
    const [closingModals, setClosingModals] = useState(new Set())

    const pushModal = (type, props = {}) => {
        const id = Date.now() + Math.random()

        // Prevent duplicate modals (helps with React StrictMode double-rendering)
        setModalStack(prev => {
            // Check if an identical modal (same type and props) was just added
            const lastModal = prev[prev.length - 1]
            if (lastModal &&
                lastModal.type === type &&
                JSON.stringify(lastModal.props) === JSON.stringify(props)) {
                // Don't add duplicate, return existing stack
                return prev
            }
            return [...prev, { id, type, props }]
        })

        // Mark modal as opening (will start with opened=false)
        setOpeningModals(prev => new Set(prev).add(id))

        // After a brief delay, mark as opened to trigger entrance animation
        setTimeout(() => {
            setOpeningModals(prev => {
                const next = new Set(prev)
                next.delete(id)
                return next
            })
        }, 10) // Small delay to ensure React renders with opened=false first

        return id
    }

    const popModal = (id) => {
        if (id !== undefined) {
            // Mark modal as closing to trigger exit animation
            setClosingModals(prev => new Set(prev).add(id))

            // After animation duration, remove from stack
            setTimeout(() => {
                setModalStack(prev => prev.filter(modal => modal.id !== id))
                setClosingModals(prev => {
                    const next = new Set(prev)
                    next.delete(id)
                    return next
                })
            }, 1000) // Match this to transitionProps duration
        } else {
            // Remove the top modal
            const topModal = modalStack[modalStack.length - 1]
            if (topModal) {
                popModal(topModal.id)
            }
        }
    }

    const clearModals = () => {
        setModalStack([])
        setOpeningModals(new Set())
        setClosingModals(new Set())
    }

    // Make pushModal globally accessible for backwards compatibility with Actions
    global.pushModal = pushModal
    global.clearModals = clearModals

    const value = {
        pushModal,
        popModal,
        clearModals,
        modalCount: modalStack.length
    }

    return (
        <ModalStackContext.Provider value={value}>
            {children}
            {modalStack.map((modal, index) => {
                const ModalComponent = MODAL_COMPONENTS[modal.type]
                if (!ModalComponent) {
                    console.error(`Unknown modal type: ${modal.type}`)
                    return null
                }

                const baseZIndex = 1000
                const zIndex = baseZIndex + (index * 10)
                const isOpening = openingModals.has(modal.id)
                const isClosing = closingModals.has(modal.id)

                // Modal should be opened=false when first added (opening state)
                // or when being closed (closing state)
                const opened = !isOpening && !isClosing

                return (
                    <ModalComponent
                        key={modal.id}
                        opened={opened}
                        onClose={() => popModal(modal.id)}
                        zIndex={zIndex}
                        transitionProps={{
                            transition: 'fade',
                            duration: 300,
                            timingFunction: 'ease-in-out'
                        }}
                        {...modal.props}
                    />
                )
            })}
        </ModalStackContext.Provider>
    )
}

export const useModalStack = () => {
    const context = useContext(ModalStackContext)
    if (!context) {
        throw new Error('useModalStack must be used within ModalStackProvider')
    }
    return context
}

